<app-title-bar
  title="Gate Events"
  description="Verify that gate-open commands are actually delivered to hardware — connects to each lane's live device stream and shows real delivery outcomes."
  [showButton]="false"
>
</app-title-bar>

<div class="internal-badge-row">
  <span class="internal-badge">Internal tool</span>
  <span class="internal-badge muted">Unlisted &middot; not in nav</span>
</div>

<div class="warning-banner">
  <strong>Connecting supersedes any live device.</strong>
  If a physical gate is already connected on a lane, watching that lane here will disconnect it
  immediately — no grace period. The lane's reported status can also be stale (a device that dropped
  without closing cleanly still reports "connected"), so it is shown for context only and never
  blocks the Connect action.
</div>

<div class="selection-section">
  <div class="selection-field">
    <label class="selection-label">Car wash</label>
    <app-simple-autocomplete
      [options]="companyOptions"
      [placeholder]="'Search car wash'"
      [selectedValue]="selectedCompanyId"
      (selectionChange)="selectCompany($event)"
    >
    </app-simple-autocomplete>
  </div>

  @if (selectedCompanyId) {
    <div class="selection-field">
      <label class="selection-label">Location</label>
      <app-simple-autocomplete
        [options]="locationOptions"
        [placeholder]="'Search location'"
        [selectedValue]="selectedLocationId"
        (selectionChange)="selectLocation($event)"
      >
      </app-simple-autocomplete>
    </div>
  }
</div>

@if (selectedLocationId) {
  <div class="lanes-section">
    <div class="section-heading">
      <h2>Lanes</h2>
      <span class="section-sub"
        >{{ lanes.length }} gate{{ lanes.length === 1 ? '' : 's' }} configured</span
      >
    </div>

    @if (loadingLanes) {
      <p class="loading-text">Loading lanes…</p>
    }

    @if (!loadingLanes && lanes.length === 0) {
      <p class="empty-text">No lanes with a gate serial configured for this location.</p>
    }

    @if (!loadingLanes && lanes.length > 0) {
      <div class="lane-grid">
        @for (lane of lanes; track lane.laneId) {
          <div
            class="lane-card"
            [class.watching]="lane.streamStatus === 'open' || lane.streamStatus === 'connecting'"
          >
            <div class="lane-card-top">
              <div>
                <div class="lane-name">{{ lane.laneName }}</div>
                <div class="lane-serial">{{ lane.gateSerial }}</div>
              </div>
            </div>

            @if (lane.streamStatus === 'open') {
              <span class="status-line watching"><span class="dot watching"></span>Watching</span>
            } @else if (lane.streamStatus === 'connecting') {
              <span class="status-line watching"
                ><span class="dot watching"></span>Connecting…</span
              >
            } @else if (lane.streamStatus === 'error') {
              <span class="status-line offline"><span class="dot offline"></span>Stream error</span>
            } @else if (lane.isCheckingStatus) {
              <span class="status-line checking">Checking status…</span>
            } @else if (lane.reportedConnected === true) {
              <span
                class="status-line hint"
                title="Can be stale — a device that dropped without closing cleanly still reports connected."
                >Last known: connected (may be stale)</span
              >
            } @else if (lane.reportedConnected === false) {
              <span
                class="status-line hint"
                title="Can be stale — a device that dropped without closing cleanly still reports connected."
                >Last known: offline (may be stale)</span
              >
            } @else {
              <span class="status-line hint">Last known status: unknown</span>
            }

            @if (lane.streamStatus === 'open' || lane.streamStatus === 'connecting') {
              <button type="button" class="lane-btn stop" (click)="onDisconnectClick(lane)">
                Disconnect
              </button>
            } @else {
              <button type="button" class="lane-btn primary" (click)="onConnectClick(lane)">
                Connect to stream
              </button>
            }
          </div>
        }
      </div>
    }
  </div>

  <div class="log-card">
    <div class="log-card-header">
      <h2>Event log</h2>
      <span class="section-sub"
        >Confirmed deliveries only — a dropped/suppressed open never reaches this stream</span
      >
    </div>

    @if (events.length === 0) {
      <p class="empty-text">No gate-open commands received yet on the connected lane(s).</p>
    }

    @if (events.length > 0) {
      <div class="event-list">
        @for (group of groupedEvents; track group.startedAt) {
          @if (group.isMultiLane) {
            <div
              class="seq-highlight"
              title="Possible staggered sequence, inferred from timing only — not a confirmed batch. Manual and automatic opens share the same per-location stagger queue and can land in the same group even when unrelated."
            >
              <span class="seq-chip">⚡ Possible sequence · {{ group.laneCount }} lanes</span>
              @for (event of group.events; track event.id) {
                <ng-container *ngTemplateOutlet="eventRow; context: { $implicit: event }">
                </ng-container>
              }
            </div>
          } @else {
            @for (event of group.events; track event.id) {
              <ng-container *ngTemplateOutlet="eventRow; context: { $implicit: event }">
              </ng-container>
            }
          }
        }
      </div>
    }
  </div>
}

<ng-template #eventRow let-event>
  <div class="event-row">
    <div class="event-time">
      {{ event.absoluteTimeLabel }}
      @if (event.deltaMs !== null) {
        <span class="delta">+{{ event.deltaMs }}ms</span>
      }
    </div>
    <div class="event-main">
      <span class="event-lane">{{ event.laneName }}</span>
      <span class="event-serial">{{ event.gateSerial }}</span>
    </div>
    @if (event.arg) {
      <span class="event-arg" title="Relay/command argument">{{ event.arg }}</span>
    }
  </div>
</ng-template>

<app-confirmation-modal
  [isVisible]="confirmModalVisible"
  [data]="confirmModalData"
  (cancel)="onCancelConnect()"
  (confirm)="onConfirmConnect()"
>
</app-confirmation-modal>
